/**
 * Evidence Safe route gate (T-162-001 wiring, US-162).
 *
 * Wraps Evidence Safe / Delivery Proof route elements at the route level
 * (App.tsx registry loop): evaluates the tier gate BEFORE rendering the
 * feature — allowed renders children, denied renders the Enterprise upgrade
 * modal (T-162-002) instead of the feature. Fail-closed while loading: the
 * children do not render until the gate answers.
 */
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
    checkEvidenceSafeAccess,
    evaluateEvidenceSafeGate,
    EVIDENCE_SAFE_ROUTES,
    EvidenceSafeGateResponse,
    EvidenceSafeFeature,
} from './guard';
import { EnterpriseUpgradeModal } from './upgrade-modal';

interface EvidenceSafeRouteGateProps {
    children: React.ReactNode;
    /** Test seam — overrides the live tier fetch. */
    checkOverride?: () => Promise<EvidenceSafeGateResponse>;
}

export const EvidenceSafeRouteGate: React.FC<EvidenceSafeRouteGateProps> = ({ children, checkOverride }) => {
    const location = useLocation();
    const navigate = useNavigate();
    const feature: EvidenceSafeFeature = EVIDENCE_SAFE_ROUTES[location.pathname] || 'evidence-safe';
    const [response, setResponse] = useState<EvidenceSafeGateResponse | null>(null);

    useEffect(() => {
        let mounted = true;
        setResponse(null);
        const check = checkOverride ? checkOverride() : checkEvidenceSafeAccess(feature);
        check
            .then((r) => {
                if (mounted) setResponse(r);
            })
            .catch(() => {
                // Fail-closed: a rejected check denies rather than hangs.
                if (mounted) setResponse(evaluateEvidenceSafeGate(null, feature));
            });
        return () => {
            mounted = false;
        };
    }, [feature, checkOverride]);

    if (!response) {
        return (
            <div className="max-w-7xl mx-auto px-4 py-16 text-center text-gray-500" data-testid="evidence-safe-checking">
                Checking your plan…
            </div>
        );
    }

    if (response.allowed) return <>{children}</>;

    return (
        <EnterpriseUpgradeModal
            isOpen
            gateResponse={response}
            onClose={() => navigate('/dashboard')}
        />
    );
};

/** Convenience: evaluate a tier synchronously for callers that already hold it. */
export { evaluateEvidenceSafeGate };
export { EnterpriseUpgradeModal };
export * from './guard';
export default EvidenceSafeRouteGate;
